import { addComment } from "@/modules/lead-page/api/lead-page.api";
import { useContext, useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import useAuth from "@/modules/common/hooks/auth.hook";
import { queryCommonFlags } from "@/api/constants";
import { getMentionList } from "@/modules/lead-page/api/lead-page.v2.api";
import { toast } from "react-toastify";
import { useScrollAfterAction } from "@/modules/lead-page/new/hooks/scroll-after-action.hook";
import { getTracks } from "@/modules/property-tracks/api";

const useAddComments = () => {
  const { user } = useAuth();
  const [mentionIds, setMentionIds] = useState<string>("");
  const [formatedComment, setFormatedComment] = useState<string>("");
  const [commentMessage, setCommentMessage] = useState<string>("");
  const [mentionedUsers, setMentionedUsers] = useState<any[]>([]);

  const [dialogOpenFeedback, setDialogOpenFeedback] = useState(false);
  const [addCommentmobile, setAddCommentMobile] = useState<boolean>(false);
  const [notifyResaleAdmin, setNotify] = useState<boolean>(false);
  const [selectedTrackUId, setSelectedTrackUId] = useState<string | null>(null);
  const [showTrackSelectionModal, setShowTrackSelectionModal] = useState(false);
  const { lead } = useContext(LeadPageContext);
  const journeyUId = lead?.data?.lead?.journey_uid;

  const { data: mention_list } = useQuery({
    queryKey: ["getUsersWithAccess", lead?.data.lead.journey_uid],
    queryFn: () => getMentionList(journeyUId),
    ...queryCommonFlags,
    enabled: !!journeyUId,
  });

  const {
    data: tracksData,
    isLoading: isLoadingTracks,
    isError: isTracksError,
  } = useQuery({
    queryKey: ["getTracksForJourney", journeyUId],
    queryFn: () =>
      getTracks({
        filters: {
          journeyUId: journeyUId!,
        },
        pagination: {
          page: 1,
          pageSize: 100,
          orderBy: "createdAt",
        },
      }),
    ...queryCommonFlags,
    enabled: !!journeyUId && notifyResaleAdmin,
  });

  const addCommentMutation = useMutation((data: any) => addComment(data), {
    onSuccess: () => {
      setNotify(false);

      // Clear activity cache to reset pagination - this must happen before scroll hook runs
      const leadNanoId = lead?.data?.nanoId;
      if (leadNanoId) {
        queryClient.removeQueries({
          queryKey: ["lead-activity", leadNanoId],
          exact: false,
        });
      }

      // Only invalidate old comment views
      setSelectedTrackUId(null);
      setShowTrackSelectionModal(false);
      queryClient.invalidateQueries({ queryKey: ["getComments"] });

      setAddCommentMobile(false);
      setCommentMessage("");

      toast.success("Comment added successfully", {
        position: "bottom-left",
        autoClose: 3000,
      });
    },
    onError: () => {
      setNotify(false);
      toast.error("Failed to add comment", {
        position: "bottom-left",
        autoClose: 3000,
      });
    },
  });
  useScrollAfterAction(addCommentMutation);

  // Handle track selection when notify checkbox changes
  useEffect(() => {
    if (notifyResaleAdmin && tracksData?.data?.results) {
      const tracks = tracksData.data.results;
      if (tracks.length === 1) {
        // Auto-select if only one track
        setSelectedTrackUId(tracks[0].uid);
        setShowTrackSelectionModal(false);
      } else if (tracks.length > 1) {
        // Show selection modal if multiple tracks
        setShowTrackSelectionModal(true);
        setSelectedTrackUId(null);
      } else {
        // No tracks found
        setSelectedTrackUId(null);
        setShowTrackSelectionModal(false);
      }
    } else if (!notifyResaleAdmin) {
      // Reset when unchecked
      setSelectedTrackUId(null);
      setShowTrackSelectionModal(false);
    }
  }, [notifyResaleAdmin, tracksData]);

  const extractMentionIds = (text: string): string => {
    const regex = /@\[[^\]]{1,200}\]\(([\w-]+)\)/g;
    const ids = new Set<string>();

    let match;
    while ((match = regex.exec(text)) !== null) {
      ids.add(match[1]);
    }

    return Array.from(ids).join(",");
  };

  const handleTextareaChange = (event: { target: { value: string } }) => {
    const newMessage = event.target.value;
    setCommentMessage(newMessage);

    setFormatedComment(newMessage);

    setMentionIds(extractMentionIds(event.target.value));

    if (mention_list?.data) {
      setMentionedUsers(mention_list.data);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      commentMessage &&
      !addCommentMutation.isPending
    ) {
      event.preventDefault();
      handleAddComments();
    }
  };

  const handleAddComments = async () => {
    // If notify is checked but no track selected (and multiple tracks exist), don't proceed
    if (
      notifyResaleAdmin &&
      !selectedTrackUId &&
      tracksData?.data?.results &&
      tracksData.data.results.length > 1
    ) {
      setShowTrackSelectionModal(true);
      return;
    }

    const data: {
      comment: string;
      leadJourneyUId: string | undefined;
      notifyResaleAdmin: boolean;
      propertyTrackUIds?: string[];
      mentions?: string[];
    } = {
      comment: formatedComment,
      leadJourneyUId: lead?.data.uid,
      notifyResaleAdmin,
      mentions: [],
    };

    if (notifyResaleAdmin && selectedTrackUId) {
      data.propertyTrackUIds = [selectedTrackUId];
    }

    if (mentionIds.length > 0) {
      const idsArray = mentionIds.split(",").map((id) => id.trim());
      data.mentions = idsArray;
    }
    addCommentMutation.mutate(data);
  };

  const tracks = tracksData?.data?.results || [];

  return {
    commentMessage,
    lead_type: lead?.data.lead_type.lead_type,
    dialogOpenFeedback,
    errorAddingComment: addCommentMutation.isError,
    successAddingComment: addCommentMutation.isSuccess,
    loadAddingComment: addCommentMutation.isPending,
    mentionIds,
    mentionedUsers: mentionedUsers.map((user: any) => ({
      id: user.uid,
      display: user.name,
      color: user.color,
      name: user.name,
    })),
    addCommentmobile,
    notify: notifyResaleAdmin,
    user_id: user?.crmId,
    handleAddComments,
    handleTextareaChange,
    handleKeyPress,
    setCommentMessage,
    setMentionIds,
    setNotify,
    setAddCommentMobile,
    setDialogOpenFeedback,
    // Track selection related
    tracks,
    isLoadingTracks,
    isTracksError,
    selectedTrackUId,
    setSelectedTrackUId,
    showTrackSelectionModal,
    setShowTrackSelectionModal,
  };
};

export default useAddComments;
